let refreshPromise: Promise<string | null> | null = null;

/**
 * Attempt to refresh the auth token using the current (possibly expired) token.
 * Uses a mutex so concurrent 401s only trigger one refresh call.
 * Returns the new token on success, or null on failure.
 */
export async function refreshAuthToken(): Promise<string | null> {
  // If a refresh is already in flight, piggyback on it
  if (refreshPromise) return refreshPromise;

  refreshPromise = attemptRefresh();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function attemptRefresh(): Promise<string | null> {
  const token = localStorage.getItem('auth_token');
  if (!token) return null;

  // Validate that the token isn't older than 7 days (no point calling backend)
  try {
    const parts = token.split('.');
    const payloadPart = parts[1];
    if (!payloadPart) return null;

    const payload = JSON.parse(atob(payloadPart)) as { iat?: number };
    if (payload.iat) {
      const tokenAgeMs = Date.now() - payload.iat * 1000;
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      if (tokenAgeMs > sevenDaysMs) return null;
    }
  } catch {
    return null;
  }

  try {
    // Call refresh endpoint directly with fetch to avoid the 401 interceptor loop
    const baseUrl =
      typeof import.meta !== 'undefined' && import.meta.env
        ? ((import.meta.env as Record<string, string>)['VITE_API_URL'] ?? '/api')
        : '/api';

    const resp = await fetch(`${baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    if (!resp.ok) return null;

    const data = (await resp.json()) as { access_token?: string };
    if (data.access_token) {
      localStorage.setItem('auth_token', data.access_token);
      return data.access_token;
    }

    return null;
  } catch {
    return null;
  }
}
